"use client";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import { Price } from "@/components/Price";
import { StarRating } from "@/components/reviews/StarRating";
import { QuickIssueChips } from "@/components/reviews/QuickIssueChips";
import { ItemRatingRow } from "@/components/reviews/ItemRatingRow";
import { ApologyOfferCard } from "@/components/reviews/ApologyOfferCard";
import { GooglePrompt } from "@/components/reviews/GooglePrompt";
import {
  reviewApi,
  canAttemptReview,
  type Review,
  type ReviewEligibility,
  type ReviewItemRating,
} from "@/api/reviewApi";
import { ApiError } from "@/api/http";
import { SignInModal } from "@/components/SignInModal";
import { useAuth } from "@/store/auth";

const FREE_TEXT_MAX = 2000;

type Phase =
  | "loading"
  | "form"
  | "submitting"
  | "submitted"
  | "already_reviewed"
  | "not_eligible"
  | "error"
  | "sign_in";

function starHelperKey(rating: number): string {
  if (rating <= 2) return "v2.reviews.helper.not_great";
  if (rating === 3) return "v2.reviews.helper.okay";
  return "v2.reviews.helper.loved_it";
}

/**
 * Review submission screen (Block 7 M1, Task 8).
 *
 * Loads eligibility for the order, then branches on the star tap:
 * 1-3★ → fix-it path (free text + quick-issue chips); 4-5★ → celebrate path
 * (free text + optional per-item ratings). Submit routes to a post-submit
 * state keyed off the returned review's sentiment.
 */
export function ReviewSubmitScreen() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { t, i18n } = useTranslation();
  const { isLoggedIn } = useAuth();

  const [phase, setPhase] = React.useState<Phase>("loading");
  const [signInOpen, setSignInOpen] = React.useState(false);
  const [eligibility, setEligibility] = React.useState<ReviewEligibility | null>(null);
  const [rating, setRating] = React.useState(0);
  const [freeText, setFreeText] = React.useState("");
  const [quickIssues, setQuickIssues] = React.useState<string[]>([]);
  const [itemRatings, setItemRatings] = React.useState<Record<string, number>>({});
  const [submittedReview, setSubmittedReview] = React.useState<Review | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Bumped to re-run the effect below when the user taps "Try again" from
  // the error state — a plain retry function can't live outside the effect
  // since it needs the same cancellation guard.
  const [retryCount, setRetryCount] = React.useState(0);
  const retry = () => setRetryCount((n) => n + 1);

  React.useEffect(() => {
    let cancelled = false;
    if (!orderId) {
      setPhase("not_eligible");
      return;
    }
    // No customer identity (anonymous dine-in payer / unauthenticated
    // group-cart guest) → the BFF's eligibility route requires a customer
    // JWT and 401s unconditionally (see reviewApi.canAttemptReview). This
    // screen can be landed on directly, so show a real "sign in" state
    // instead of silently rendering nothing. Depends on `isLoggedIn` so it
    // re-runs the moment the user signs in via the modal below.
    if (!canAttemptReview()) {
      setPhase("sign_in");
      return;
    }
    setPhase("loading");
    reviewApi
      .getEligibility(orderId)
      .then((res) => {
        if (cancelled) return;
        setEligibility(res);
        if (!res.eligible) {
          setPhase(res.existingReview ? "already_reviewed" : "not_eligible");
        } else {
          setPhase("form");
        }
      })
      .catch(() => {
        // Any transport/server failure (404/500/network) is a genuine error,
        // not the customer's order simply being ineligible — those two must
        // stay visually and semantically distinct so a flaky request doesn't
        // read as "you can't review this."
        if (cancelled) return;
        setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, retryCount, isLoggedIn]);

  const toggleQuickIssue = (id: string) => {
    setQuickIssues((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setItemRating = (itemId: string, n: number) => {
    setItemRatings((prev) => ({ ...prev, [itemId]: n }));
  };

  const submit = async () => {
    if (!orderId || rating === 0) return;
    setPhase("submitting");
    setSubmitError(null);

    // Gate by sentiment at submit time (not just "is the field non-empty") so
    // stale chip/item-rating state from a prior star tap in this session
    // never rides along after the user changes their mind — e.g. picking
    // quick-issue chips at 2★ then raising to 5★ before submitting.
    const negative = rating > 0 && rating <= 3;
    const positive = rating >= 4;

    const items = eligibility?.orderContext?.items ?? [];
    const builtItemRatings: ReviewItemRating[] = positive
      ? items
          .filter((it) => itemRatings[it.itemId] > 0)
          .map((it) => ({ itemId: it.itemId, itemName: it.name, rating: itemRatings[it.itemId] }))
      : [];

    try {
      const review = await reviewApi.submit(orderId, {
        rating,
        freeText: freeText.trim() ? freeText.trim() : undefined,
        itemRatings: builtItemRatings.length > 0 ? builtItemRatings : undefined,
        quickIssues: negative && quickIssues.length > 0 ? quickIssues : undefined,
        source: "in_app",
      });
      setSubmittedReview(review);
      setPhase("submitted");
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setPhase("already_reviewed");
        return;
      }
      setSubmitError(t("v2.reviews.submit_error", "Couldn't send your feedback. Please try again."));
      setPhase("form");
    }
  };

  const goBack = () => navigate(orderId ? `/orders/${orderId}` : "/history");

  const dateFmt = React.useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" }),
    [i18n.language],
  );

  /* ── Loading ── */
  if (phase === "loading") {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.title", "Rate your order")} onBack={goBack} />
        <div style={{ padding: 16 }} data-testid="review-skeleton">
          <Card padding={14} style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: "var(--surface-2)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 13, width: "60%", borderRadius: 6, background: "var(--surface-2)", marginBottom: 9 }} />
              <div style={{ height: 11, width: "42%", borderRadius: 6, background: "var(--surface-2)" }} />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  /* ── Sign in required (no customer identity yet) ── */
  if (phase === "sign_in") {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.title", "Rate your order")} onBack={goBack} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "64px 32px 24px",
            gap: 4,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              marginBottom: 20,
              color: "var(--primary-700)",
            }}
          >
            <Icons.receipt size={32} />
          </div>
          <div className="display" style={{ fontWeight: 700, fontSize: 20, color: "var(--ink-1)", marginBottom: 10 }}>
            {t("v2.reviews.sign_in.title", "Sign in to leave a review")}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-2)", maxWidth: 270 }}>
            {t(
              "v2.reviews.sign_in.body",
              "Reviews are tied to your account, so we need you signed in before you can rate this order.",
            )}
          </div>
        </div>
        <div style={{ padding: "14px 16px 22px" }}>
          <Button variant="primary" size="lg" full onClick={() => setSignInOpen(true)}>
            {t("v2.reviews.sign_in.cta", "Sign in")}
          </Button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  /* ── Already reviewed (read-only) ── */
  if (phase === "already_reviewed") {
    const existing = eligibility?.existingReview;
    const ctx = eligibility?.orderContext;
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.your_review", "Your review")} onBack={goBack} />
        {ctx && (
          <div style={{ display: "flex", gap: 12, padding: 16, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 12,
                background: "linear-gradient(135deg, var(--primary-50), var(--primary-100, #c4ebd7))",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icons.leaf size={24} stroke="var(--primary-700)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="display" style={{ fontWeight: 700, fontSize: 15, color: "var(--ink-1)" }}>
                {ctx.branchName}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2 }}>
                {t("v2.reviews.order_number", "Order #{{n}}", { n: ctx.orderNumber })} · <Price amount={ctx.totalAmount} />
              </div>
            </div>
          </div>
        )}
        <div style={{ padding: 16 }}>
          {existing && (
            <Card padding={18}>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: 0.06,
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                  marginBottom: 9,
                }}
              >
                {existing.createdAt
                  ? t("v2.reviews.reviewed_on", "You reviewed this on {{date}}", {
                      date: dateFmt.format(new Date(existing.createdAt)),
                    })
                  : t("v2.reviews.reviewed_already", "You've already reviewed this order")}
              </div>
              <div style={{ marginBottom: 12 }}>
                <StarRating value={existing.rating} readOnly size={20} justify="flex-start" />
              </div>
              {existing.freeText && (
                <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-1)" }}>
                  &ldquo;{existing.freeText}&rdquo;
                </div>
              )}
            </Card>
          )}
          <div style={{ textAlign: "center", marginTop: 18, fontSize: 12.5, color: "var(--ink-3)" }}>
            {t("v2.reviews.one_per_order", "You can only review an order once.")}
          </div>
        </div>
      </div>
    );
  }

  /* ── Not eligible ── */
  if (phase === "not_eligible") {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.title", "Rate your order")} onBack={goBack} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "64px 32px 24px",
            gap: 4,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              marginBottom: 20,
            }}
          >
            <Icons.info size={34} stroke="var(--ink-3)" />
          </div>
          <div className="display" style={{ fontWeight: 700, fontSize: 20, color: "var(--ink-1)", marginBottom: 10 }}>
            {t("v2.reviews.not_eligible.title", "This order isn't available for review")}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-2)", maxWidth: 270 }}>
            {t(
              "v2.reviews.not_eligible.body",
              "Orders can be reviewed within 30 days of completion. Cancelled and refunded orders aren't eligible.",
            )}
          </div>
        </div>
        <div style={{ padding: "14px 16px 22px" }}>
          <Button variant="outline" size="lg" full onClick={goBack}>
            {t("v2.reviews.not_eligible.back", "Back to orders")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Error (eligibility load failed — distinct from "not eligible") ── */
  if (phase === "error") {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.title", "Rate your order")} onBack={goBack} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "64px 32px 24px",
            gap: 4,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              marginBottom: 20,
            }}
          >
            <Icons.info size={34} stroke="var(--ink-3)" />
          </div>
          <div className="display" style={{ fontWeight: 700, fontSize: 20, color: "var(--ink-1)", marginBottom: 10 }}>
            {t("v2.reviews.error.title", "Something went wrong")}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-2)", maxWidth: 270 }}>
            {t("v2.reviews.error.body", "We couldn't load this review. Check your connection and try again.")}
          </div>
        </div>
        <div style={{ padding: "14px 16px 22px" }}>
          <Button variant="primary" size="lg" full onClick={retry}>
            {t("v2.reviews.error.retry", "Try again")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Post-submit (branches on sentiment) ── */
  if (phase === "submitted" && submittedReview) {
    const sentiment = submittedReview.sentiment;
    const branchName = eligibility?.orderContext?.branchName;

    if (sentiment === "negative") {
      return (
        <div style={{ paddingBottom: 80 }}>
          <TopBar title={t("v2.reviews.title", "Rate your order")} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "40px 24px 0",
              gap: 0,
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 999,
                background: "var(--primary-50)",
                display: "grid",
                placeItems: "center",
                marginBottom: 20,
              }}
            >
              <Icons.check size={34} stroke="var(--primary-700)" sw={2.5} />
            </div>
            <div className="display" style={{ fontWeight: 700, fontSize: 22, color: "var(--ink-1)", marginBottom: 10 }}>
              {t("v2.reviews.post_submit.negative.title", "Thanks for telling us")}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--ink-2)", maxWidth: 280, marginBottom: 24 }}>
              {branchName
                ? t("v2.reviews.post_submit.negative.body", "We've sent your feedback to the manager of {{branch}}. You'll hear back within 24 hours.", { branch: branchName })
                : t("v2.reviews.post_submit.negative.body_generic", "We've sent your feedback to the manager. You'll hear back within 24 hours.")}
            </div>
            {submittedReview.apologyOffer && (
              <ApologyOfferCard offer={submittedReview.apologyOffer} />
            )}
          </div>
          <div style={{ padding: "14px 16px 22px" }}>
            <Button variant="outline" size="lg" full onClick={goBack}>
              {t("v2.reviews.done", "Done")}
            </Button>
          </div>
        </div>
      );
    }

    if (sentiment === "positive") {
      return (
        <div style={{ paddingBottom: 80 }}>
          <TopBar title={t("v2.reviews.title", "Rate your order")} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px 28px 24px",
              gap: 0,
            }}
          >
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: 999,
                background: "linear-gradient(135deg, var(--primary-50), var(--primary-100, #c4ebd7))",
                display: "grid",
                placeItems: "center",
                marginBottom: 20,
                boxShadow: "0 6px 16px -6px rgba(9,145,74,.3)",
              }}
            >
              <Icons.star size={38} stroke="var(--primary-600)" />
            </div>
            <div className="display" style={{ fontWeight: 700, fontSize: 22, color: "var(--ink-1)", marginBottom: 10 }}>
              {t("v2.reviews.post_submit.positive.title", "Thanks for the review!")}
            </div>

            {submittedReview.googleReviewUrl && (
              <div style={{ marginBottom: 8, maxWidth: 290 }}>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)" }}>
                  <b style={{ color: "var(--ink-1)" }}>
                    {t("v2.reviews.google.ask", "Would you mind sharing on Google?")}
                  </b>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: "14px 16px 22px" }}>
            {submittedReview.googleReviewUrl ? (
              <GooglePrompt googleReviewUrl={submittedReview.googleReviewUrl} branchName={branchName} onDismiss={goBack} />
            ) : (
              <Button variant="outline" size="lg" full onClick={goBack}>
                {t("v2.reviews.done", "Done")}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // neutral
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.reviews.title", "Rate your order")} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "56px 30px 0",
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              marginBottom: 20,
            }}
          >
            <Icons.receipt size={32} stroke="var(--ink-2)" />
          </div>
          <div className="display" style={{ fontWeight: 700, fontSize: 22, color: "var(--ink-1)", marginBottom: 10 }}>
            {t("v2.reviews.post_submit.neutral.title", "Thanks for the feedback")}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)", maxWidth: 280 }}>
            {t(
              "v2.reviews.post_submit.neutral.body",
              "We've shared it with the manager. If there's anything specific we should know, let us know anytime.",
            )}
          </div>
        </div>
        <div style={{ padding: "14px 16px 22px" }}>
          <Button variant="outline" size="lg" full onClick={goBack}>
            {t("v2.reviews.done", "Done")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Form (eligible): star prompt → branch on rating ── */
  const ctx = eligibility?.orderContext;
  const items = ctx?.items ?? [];
  const submitting = phase === "submitting";
  const isNegative = rating > 0 && rating <= 3;
  const isPositive = rating >= 4;

  return (
    <div style={{ paddingBottom: 80, display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <TopBar title={t("v2.reviews.title", "Rate your order")} onBack={goBack} />

      {rating === 0 && ctx && (
        <div style={{ display: "flex", gap: 12, padding: 16, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: "linear-gradient(135deg, var(--primary-50), var(--primary-100, #c4ebd7))",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icons.leaf size={24} stroke="var(--primary-700)" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="display" style={{ fontWeight: 700, fontSize: 15, color: "var(--ink-1)" }}>
              {ctx.branchName}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2 }}>
              {t("v2.reviews.order_number", "Order #{{n}}", { n: ctx.orderNumber })}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", marginTop: 1 }}>
              <Price amount={ctx.totalAmount} />
              {items.length > 0 && ` · ${items.map((it) => it.name).join(", ")}`}
            </div>
          </div>
        </div>
      )}

      {rating === 0 ? (
        <div style={{ padding: "54px 24px 0", textAlign: "center", flex: 1 }}>
          <div className="display" style={{ fontWeight: 700, fontSize: 23, color: "var(--ink-1)", marginBottom: 30 }}>
            {t("v2.reviews.prompt", "How was your order?")}
          </div>
          <StarRating value={rating} onChange={setRating} helperText={t("v2.reviews.tap_a_star", "Tap a star")} />
        </div>
      ) : (
        <div style={{ flex: 1, overflow: "hidden", padding: "0 16px" }}>
          <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
            <StarRating value={rating} onChange={setRating} size={34} />
            <div
              className="display"
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: isPositive ? "var(--primary-700)" : "var(--ink-2)",
              }}
            >
              {t(starHelperKey(rating))}
            </div>
          </div>

          <div className="display" style={{ fontWeight: 700, fontSize: 18, color: "var(--ink-1)", marginTop: 14 }}>
            {isNegative
              ? t("v2.reviews.negative.heading", "Sorry to hear that. What went wrong?")
              : t("v2.reviews.positive.heading", "What did you love?")}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-3)", margin: "4px 0 12px" }}>
            {isNegative
              ? t("v2.reviews.negative.helper", "Optional — but it helps us fix it.")
              : t("v2.reviews.positive.helper", "Optional")}
          </div>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value.slice(0, FREE_TEXT_MAX))}
            placeholder={t("v2.reviews.free_text_placeholder", "Tell us briefly…") as string}
            maxLength={FREE_TEXT_MAX}
            rows={isNegative ? 4 : 3}
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: "var(--card-bg)",
              border: "1px solid var(--border-strong)",
              borderRadius: "var(--radius-card)",
              padding: 13,
              fontSize: 13.5,
              fontFamily: "inherit",
              color: "var(--ink-1)",
              resize: "vertical",
            }}
          />
          <div style={{ textAlign: "end", fontSize: 11, fontWeight: 500, color: "var(--ink-3)", margin: "6px 2px 14px" }}>
            {freeText.length} / {FREE_TEXT_MAX}
          </div>

          {isNegative && (
            <QuickIssueChips selected={quickIssues} onToggle={toggleQuickIssue} />
          )}

          {isPositive && items.length > 0 && (
            <>
              <div className="display" style={{ fontWeight: 700, fontSize: 15, color: "var(--ink-1)", margin: "18px 0 4px" }}>
                {t("v2.reviews.rate_items_heading", "Rate the items")}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-3)", marginBottom: 10 }}>
                {t("v2.reviews.rate_items_helper", "Optional — helps others choose.")}
              </div>
              <Card padding={0}>
                {items.map((it, i) => (
                  <React.Fragment key={it.itemId}>
                    <ItemRatingRow
                      itemName={it.name}
                      value={itemRatings[it.itemId] ?? 0}
                      onChange={(n) => setItemRating(it.itemId, n)}
                    />
                    {i < items.length - 1 && <div style={{ height: 1, background: "var(--border)", margin: "0 13px" }} />}
                  </React.Fragment>
                ))}
              </Card>
            </>
          )}

          {submitError && (
            <div style={{ marginTop: 14, fontSize: 12.5, color: "var(--danger-700, #b91c1c)", textAlign: "center" }}>
              {submitError}
            </div>
          )}

          <div style={{ height: 16 }} />
        </div>
      )}

      {rating > 0 && (
        <div
          style={{
            padding: "14px 16px 20px",
            borderTop: "1px solid var(--border)",
            background: "var(--surface-1)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Button variant="primary" size="lg" full disabled={submitting} onClick={submit}>
            {submitting ? t("v2.reviews.sending", "Sending…") : t("v2.reviews.submit", "Submit feedback")}
          </Button>
          {isNegative && (
            <Button variant="ghost" size="md" full disabled={submitting} onClick={goBack}>
              {t("v2.reviews.maybe_later", "Maybe later")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
